import { useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PROJECTS } from "@/data/site";

const IMAGES = [
  {
    src: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "Laptop with portal application code being developed",
  },
  {
    src: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "Server infrastructure powering the booking platform",
  },
  {
    src: "https://images.unsplash.com/photo-1681949103006-70066fb25dfe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "Team mapping HR portal workflows on a whiteboard",
  },
  {
    src: "https://images.unsplash.com/photo-1568992687947-868a62a9f521?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "Team collaborating on the e-commerce platform build",
  },
  {
    src: "https://images.unsplash.com/photo-1584169417032-d34e8d805e8b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "Secure data centre hosting the vendor portal",
  },
  {
    src: "https://images.unsplash.com/photo-1614741118887-7a4ee193a5fa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "Analytics dashboard code on a development display",
  },
];

export default function Portfolio() {
  const [open, setOpen] = useState(null);
  const project = PROJECTS.find((p) => p.title === open) || null;

  return (
    <>
      <Seo
        title="Case Studies | Web Portals & Platforms We Build — LKTH LTD"
        description="Representative LKTH LTD case studies: client portals, booking systems, HR portals, e-commerce platforms and analytics dashboards built for UK businesses."
      />

      <section className="relative overflow-hidden bg-[#0B1F3A] pt-[72px]">
        <div className="absolute inset-0 grid-bg" aria-hidden="true" />
        <div className="absolute inset-0 hero-glow" aria-hidden="true" />
        <div className="container-x relative py-16 sm:py-24">
          <p className="eyebrow eyebrow-light font-mono-alt">Case Studies</p>
          <h1 className="mt-4 max-w-2xl font-display text-4xl font-bold text-white sm:text-5xl">
            Platforms we have built
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300">
            Representative examples of the portals, platforms and dashboards we deliver for UK
            businesses. Client details are anonymised — references are available on request.
          </p>
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-16 sm:py-20" data-testid="portfolio-grid">
        <div className="container-x grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {PROJECTS.map((p, i) => (
            <Reveal key={p.title} delay={0.05 * (i % 3)} className="h-full">
              <article className="card-soft group flex h-full flex-col overflow-hidden">
                <div className="relative aspect-[16/10] overflow-hidden bg-[#0B1F3A]">
                  <img
                    src={IMAGES[i].src}
                    alt={IMAGES[i].alt}
                    loading="lazy"
                    className="h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A]/70 via-transparent to-transparent" aria-hidden="true" />
                  <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1 text-[11px] font-semibold text-[#0B1F3A]">
                    {p.category}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h2 className="font-display text-lg font-semibold text-[#0B1F3A]">{p.title}</h2>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{p.summary}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {p.stack.map((t) => (
                      <span key={t} className="rounded-md bg-blue-600/10 px-2 py-1 font-mono-alt text-[10px] font-medium text-blue-700">
                        {t}
                      </span>
                    ))}
                  </div>
                  <button
                    onClick={() => setOpen(p.title)}
                    data-testid={`project-view-details-${i}`}
                    className="mt-5 inline-flex items-center gap-1.5 self-start text-sm font-semibold text-blue-600 transition-colors hover:text-blue-700"
                  >
                    View Details <ArrowUpRight className="h-4 w-4" />
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-white py-14">
        <div className="container-x text-center">
          <h2 className="font-display text-2xl font-bold text-[#0B1F3A] sm:text-3xl">
            Want a platform like these?
          </h2>
          <p className="mx-auto mt-3 max-w-md text-sm text-slate-600">
            Every project starts with a free, no-obligation conversation about your goals.
          </p>
          <Link to="/contact" className="btn-primary mt-7" data-testid="portfolio-cta">
            Start Your Project <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <Dialog open={!!project} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-lg rounded-2xl" data-testid="project-detail-dialog">
          {project && (
            <>
              <DialogHeader>
                <span className="eyebrow font-mono-alt">{project.category}</span>
                <DialogTitle className="font-display text-2xl text-[#0B1F3A]">{project.title}</DialogTitle>
                <DialogDescription className="text-sm leading-relaxed text-slate-600">
                  {project.summary} Built with modern, well-supported technology and delivered with
                  documentation, training and ongoing support.
                </DialogDescription>
              </DialogHeader>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {project.stack.map((t) => (
                  <span key={t} className="rounded-md bg-blue-600/10 px-2 py-1 font-mono-alt text-[10px] font-medium text-blue-700">
                    {t}
                  </span>
                ))}
              </div>
              <Link to="/contact" className="btn-primary mt-4 w-full" data-testid="project-dialog-cta">
                Start a Similar Project <ArrowRight className="h-4 w-4" />
              </Link>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
